import { isLookSystem } from "@/utils/look-gate";

export const systemFontFamily =
  '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif';

/**
 * A form field's font on the look system: the preset's own family, which the fields drew before the website moved and
 * which Skrift's text font sets. --body-font-family differs from it for a preset whose text is in system fonts.
 */
export const LOOK_FIELD_FONT = "var(--font-family)";

/**
 * GetFont's presets (helper.ts) and Prata og systemskrift, which GetFont draws with its default and themeFontRoles
 * pairs: the only keys `theme.styleSystem.type` may name.
 */
export const FONT_TYPES: readonly string[] = [
  "classic",
  "modern",
  "inter",
  "lato",
  "titillium_web",
  "montserrat",
  "merriweather",
  "cormorantGaramond",
  "plusJakartaSans",
  "roboto-montserrat",
  "montserrat-only",
  "quicksand",
  "arya",
  "prata",
  "prata-system-font",
  "bodoniModa",
  "bodoniModa-system-font",
  "linotypeDidot-system-font",
  "playfairDisplay",
  "dmSans",
  "fraunces",
  "jost",
  "workSans",
  "playfairDisplay-dmSans",
  "fraunces-jost",
  "cormorantGaramond-workSans",
];

const ADOBE_FONT = "linotypeDidot-system-font";

function usesAdobeFont(theme?: { fontType?: string }) {
  if (theme?.fontType === ADOBE_FONT) return true;
  const type = isLookSystem(theme) ? theme.styleSystem?.type : undefined;
  return type?.heading === ADOBE_FONT || type?.body === ADOBE_FONT;
}

/** Only the opt-in Adobe preset can load a customer-supplied web project. */
export function themeFontStylesheet(theme?: {
  fontType?: string;
  adobeFontsProjectId?: string;
}) {
  const project = theme?.adobeFontsProjectId;
  return usesAdobeFont(theme) &&
    typeof project === "string" &&
    project.length === 7 &&
    /^[a-z0-9]{7}$/.test(project)
    ? `https://use.typekit.net/${project}.css`
    : undefined;
}

/** Heading/body pairings match the existing website font presets. */
export function themeFontRoles(fontType: string, bodyFont: string) {
  const headings = new Map([
    ["montserrat", '"Merriweather", serif'],
    ["roboto-montserrat", '"Roboto Slab", serif'],
    ["plusJakartaSans", '"Cormorant Garamond", serif'],
    ["prata-system-font", '"Prata", serif'],
    ["bodoniModa-system-font", '"Bodoni Moda", serif'],
    ["linotypeDidot-system-font", '"linotype-didot", serif'],
    ["playfairDisplay-dmSans", '"Playfair Display", serif'],
    ["fraunces-jost", '"Fraunces", serif'],
    ["cormorantGaramond-workSans", '"Cormorant Garamond", serif'],
  ]);
  return {
    body:
      fontType === "linotypeDidot-system-font"
        ? systemFontFamily
        : fontType === "prata-system-font" ||
            fontType === "bodoniModa-system-font"
          ? '-apple-system, system-ui, "Segoe UI", Roboto, Ubuntu, sans-serif'
          : bodyFont,
    heading: headings.get(fontType) || "var(--body-font-family)",
  };
}
